import {
  Box,
  Checkbox,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import { useState } from 'react';
import { HelivanBackButton } from '../helivan/HelivanBackButton';
import { HelivanButton } from '../helivan/HelivanButton';
import { HelivanColor } from '../helivan/HelivanColor';
import { HelivanSelector } from '../helivan/HelivanSelector';
import { HelivanText } from '../helivan/HelivanText';

type ActionType = 'contact' | 'alerts' | undefined;

interface TakeActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  allDigitalTwins: { id: string; name: string }[];
  subtopics?: { name: string }[];
}

export const TakeActionModal = ({
  isOpen,
  onClose,
  allDigitalTwins,
  subtopics = [],
}: TakeActionModalProps) => {
  const [actionType, setActionType] = useState<ActionType>();
  const [useCase, setUseCase] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [selectedTwins, setSelectedTwins] = useState<Set<string>>(new Set());
  const [selectedSubtopics, setSelectedSubtopics] = useState<Set<string>>(
    new Set()
  );
  const [alertsEnabled, setAlertsEnabled] = useState(false);

  const customOnClose = () => {
    setActionType(undefined);
    setUseCase('');
    setIsLoading(false);
    setShowResults(false);
    setSelectedTwins(new Set());
    setSelectedSubtopics(new Set());
    setAlertsEnabled(false);
    onClose();
  };

  const handleContactSubmit = async () => {
    setIsLoading(true);
    // Simulate loading for 3 seconds
    setTimeout(() => {
      setIsLoading(false);
      setShowResults(true);
    }, 3000);
  };

  const handleAlertsSubmit = () => {
    setAlertsEnabled(true);
  };

  const toggleTwin = (twinId: string) => {
    const newSet = new Set(selectedTwins);
    if (newSet.has(twinId)) {
      newSet.delete(twinId);
    } else {
      newSet.add(twinId);
    }
    setSelectedTwins(newSet);
  };

  const toggleSubtopic = (subtopicName: string) => {
    const newSet = new Set(selectedSubtopics);
    if (newSet.has(subtopicName)) {
      newSet.delete(subtopicName);
    } else {
      newSet.add(subtopicName);
    }
    setSelectedSubtopics(newSet);
  };

  return (
    <Modal isOpen={isOpen} onClose={customOnClose} size="lg">
      <ModalOverlay />
      <ModalContent pb={6} maxH="90vh">
        <ModalHeader>
          <Flex width="full" alignItems="center" justifyContent="space-between">
            <HelivanBackButton onClick={customOnClose} />
            <ModalCloseButton />
          </Flex>
        </ModalHeader>
        <ModalBody>
          <Stack spacing={6} pt={4}>
            <HelivanText
              variant="pageHeader"
              colorOverride="#114471"
              textAlign="center"
            >
              Take Action
            </HelivanText>

            {!actionType && (
              <HelivanSelector
                options={[
                  { value: 'Target contact list', key: 'contact' },
                  { value: 'Enable change alerts', key: 'alerts' },
                ]}
                onSelect={(value) => setActionType(value as ActionType)}
                question="What are you looking for?"
                selectedOption={actionType}
              />
            )}

            {actionType === 'contact' && !showResults && (
              <Stack spacing={4} width="100%">
                <HelivanText variant="largeSubheader">
                  Describe who you are and what your use case is
                </HelivanText>
                <Textarea
                  height="120px"
                  bg={HelivanColor.background.secondary}
                  borderRadius="8px"
                  width="100%"
                  placeholder="e.g., I'm a policy analyst looking to understand stakeholder positions on climate legislation..."
                  onChange={(e) => setUseCase(e.target.value)}
                  value={useCase}
                />
                <Flex justifyContent="flex-end">
                  <HelivanButton
                    variant="action"
                    size="sm"
                    isDisabled={!useCase || isLoading}
                    onClick={handleContactSubmit}
                  >
                    {isLoading ? <Spinner size="xs" /> : 'Generate'}
                  </HelivanButton>
                </Flex>
              </Stack>
            )}

            {actionType === 'contact' && showResults && (
              <Box
                p={4}
                bg={HelivanColor.background.secondary}
                borderRadius="8px"
                fontFamily="monospace"
                fontSize="sm"
                whiteSpace="pre-wrap"
              >
                {`🎯 PRIORITY TARGETS (Next 2 Weeks)

1. Senator Johnson (Tier 1)
   - Influence Score: 8.2/10
   - Persuasion Probability: 73%
   - Approach: Economic impact angle
   - Best Contact: Via Chief of Staff
   - Timing: Before Thursday vote

2. CEO Martinez (Tier 2)
   - Influence Score: 7.1/10
   - Persuasion Probability: 45%
   - Approach: Innovation competitiveness
   - Best Contact: Conference introduction
   - Timing: Post-earnings call

🤝 COALITION OPPORTUNITIES
- Group: Tech policy moderates
- Strategy: Joint position paper
- Coordinator: Rep. Kim (already aligned)`}
              </Box>
            )}

            {actionType === 'alerts' && !alertsEnabled && (
              <Stack spacing={4} width="100%">
                <Stack spacing={4}>
                  <Stack spacing={1}>
                    <HelivanText variant="largeSubheader">
                      Enable persona alerts
                    </HelivanText>
                    <HelivanText variant="smallBody">
                      When a persona's opinion shifts on these topics, you will
                      receive an alert.
                    </HelivanText>
                  </Stack>
                  <Box
                    maxH="200px"
                    overflowY="auto"
                    p={3}
                    bg={HelivanColor.background.secondary}
                    borderRadius="8px"
                  >
                    <VStack align="stretch" spacing={2}>
                      {allDigitalTwins.map((twin) => (
                        <Checkbox
                          key={twin.id}
                          isChecked={selectedTwins.has(twin.id)}
                          onChange={() => toggleTwin(twin.id)}
                        >
                          <HelivanText variant="smallBody">
                            {twin.name}
                          </HelivanText>
                        </Checkbox>
                      ))}
                    </VStack>
                  </Box>
                </Stack>

                {subtopics.length > 0 && (
                  <Stack spacing={4}>
                    <HelivanText variant="largeSubheader">
                      Enable crowd alerts
                    </HelivanText>
                    <Box
                      maxH="200px"
                      overflowY="auto"
                      p={3}
                      bg={HelivanColor.background.secondary}
                      borderRadius="8px"
                    >
                      <VStack align="stretch" spacing={2}>
                        {subtopics.map((subtopic) => (
                          <Checkbox
                            key={subtopic.name}
                            isChecked={selectedSubtopics.has(subtopic.name)}
                            onChange={() => toggleSubtopic(subtopic.name)}
                          >
                            <HelivanText variant="smallBody">
                              Alert when crowd opinion shifts on:{' '}
                              {subtopic.name}
                            </HelivanText>
                          </Checkbox>
                        ))}
                      </VStack>
                    </Box>
                  </Stack>
                )}

                <Flex justifyContent="flex-end">
                  <HelivanButton
                    variant="action"
                    size="sm"
                    isDisabled={
                      selectedTwins.size === 0 && selectedSubtopics.size === 0
                    }
                    onClick={handleAlertsSubmit}
                  >
                    Enable
                  </HelivanButton>
                </Flex>
              </Stack>
            )}

            {actionType === 'alerts' && alertsEnabled && (
              <Flex justifyContent="center" py={8}>
                <HelivanText
                  variant="largeBody"
                  colorOverride={HelivanColor.text.blueDark}
                >
                  Notifications enabled
                </HelivanText>
              </Flex>
            )}
          </Stack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
